/**
 * @file CookieConsentBanner.js
 * @description Banner de consentimiento para la analítica (tarea 004). PostHog arranca con
 * `opt_out_capturing_by_default: true`, así que no captura nada hasta que el usuario elige aquí.
 * El estado ('pending' | 'granted' | 'denied') lo persiste PostHog en localStorage, por lo que
 * el banner solo aparece mientras la decisión esté pendiente.
 */
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import {
    isAnalyticsReady,
    getConsentStatus,
    grantAnalyticsConsent,
    denyAnalyticsConsent,
    identifyUser,
} from '../services/analytics';

const CookieConsentBanner = () => {
    const { t } = useTranslation();
    const { user, business } = useContext(AuthContext);

    // Solo se muestra si PostHog está activo (hay API key) y la decisión sigue pendiente.
    const [visible, setVisible] = useState(
        () => isAnalyticsReady() && getConsentStatus() === 'pending'
    );

    if (!visible) return null;

    const handleAccept = () => {
        grantAnalyticsConsent();
        // Re-identifica tras el opt-in: si el usuario ya estaba logueado, sus eventos posteriores
        // quedan atribuidos desde este momento (antes del consentimiento se suprimían).
        if (user) identifyUser(user, business);
        setVisible(false);
    };

    const handleDecline = () => {
        denyAnalyticsConsent();
        setVisible(false);
    };

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label={t('cookieConsent.title')}
            className="cookie-consent-banner"
            style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'var(--ev-action-primary-bg)', // Azul primario de la marca
                color: 'var(--ev-action-primary-fg)',
                padding: '1rem 1.25rem',
                zIndex: 9998, // por debajo del banner de impersonación (9999)
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                boxShadow: '0 -2px 12px rgba(0,0,0,0.25)',
                flexWrap: 'wrap',
            }}
        >
            <p className="mb-0" style={{ flex: '1 1 320px', fontSize: '0.9rem', lineHeight: 1.4 }}>
                {t('cookieConsent.message')}{' '}
                <Link
                    to="/privacy-policy"
                    style={{ color: 'var(--ev-action-primary-fg)', textDecoration: 'underline' }}
                >
                    {t('cookieConsent.learnMore')}
                </Link>
            </p>

            <div className="d-flex gap-2 flex-shrink-0">
                <Button
                    variant="outline-light"
                    size="sm"
                    onClick={handleDecline}
                    className="fw-semibold"
                >
                    {t('cookieConsent.decline')}
                </Button>
                <Button
                    variant="light"
                    size="sm"
                    onClick={handleAccept}
                    className="fw-bold"
                    style={{ color: 'var(--ev-text-brand)' }}
                >
                    {t('cookieConsent.accept')}
                </Button>
            </div>

            <style>{`
                @media (max-width: 576px) {
                    .cookie-consent-banner {
                        flex-direction: column;
                        align-items: stretch !important;
                        text-align: center;
                    }
                    .cookie-consent-banner .d-flex {
                        justify-content: center;
                    }
                    .cookie-consent-banner p {
                        /* En fila (desktop), flex-basis:320px fija el ANCHO mínimo del texto.
                           Al pasar a columna aquí, ese mismo flex-basis pasa a fijar la ALTURA
                           mínima (eje principal ahora es vertical), dejando un hueco enorme
                           entre el texto y los botones. Se resetea para que tome su alto natural. */
                        flex: 1 1 auto !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default CookieConsentBanner;
